import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  message,
  Row,
  Spin,
  Table,
  Typography,
} from 'antd';
import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import moment from 'moment';
import { checkToken } from 'api/utils';
import { getSujetosDeNomina } from 'api/catalogos/admnistracionDePlazas';
import { getEmpleadosWithoutFilter } from 'api/estructuraOrganizacional/empleados';
import { getBecariosWithoutFilter } from 'api/catalogos/Becario';
import { getHonorariosAsimiladosWithoutFilter } from 'api/catalogos/HonorarioAsimilado';
import {
  getPeriodosPorColaborador,
  getReporteNominasPorColaborador,
} from 'api/nominas/reporteNominasPorColaborador';
import Select from 'components/Select';
import currencyFormatter from 'utils/currencyFormatter';
import { onError } from 'utils/handlers';

export const nominasPorColaboradorPermission = {
  permissionModel: null,
  catalogsModels: ['nomina'],
  tabsModels: [],
};

const SUJETOS_DE_NOMINA_FALLBACK = [
  { slug: 'empleado', etiqueta: 'Empleado' },
  { slug: 'becario', etiqueta: 'Becario' },
  { slug: 'honorarioasimilado', etiqueta: 'Honorario Asimilado' },
];

const NUMERO_FIELD_POR_SUJETO = {
  empleado: 'numero_de_empleado',
  becario: 'numero_de_becario',
  honorarioasimilado: 'numero_de_honorario_asimilado',
};

const getColaboradoresPorSujeto = (sujeto) => {
  if (sujeto === 'becario') return getBecariosWithoutFilter();
  if (sujeto === 'honorarioasimilado') return getHonorariosAsimiladosWithoutFilter();
  return getEmpleadosWithoutFilter();
};

const renderColaborador = (sujeto) => (item) => item?.numero_y_nombre_completo
  || `${item?.[NUMERO_FIELD_POR_SUJETO[sujeto]] || ''} - ${item?.nombre || ''} ${item?.apellido_paterno || ''} ${item?.apellido_materno || ''}`.trim();

// Últimas columnas del reporte, en este orden y con títulos en mayúscula (requisito).
const COLUMNAS_TOTALES = [
  { key: 'total_percepciones', title: 'TOTAL PERCEPCIONES' },
  { key: 'total_deducciones', title: 'TOTAL DEDUCCIONES' },
  { key: 'total_descuentos', title: 'TOTAL DESCUENTOS' },
  { key: 'neto_a_pagar', title: 'NETO A PAGAR' },
];

const FORMATO_MONEDA_EXCEL = '"$"#,##0.00;-"$"#,##0.00';

const redondear = (valor) => Math.round((Number(valor) || 0) * 100) / 100;

const descargarBlobComoArchivo = (blob, nombreArchivo) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  link.click();
  URL.revokeObjectURL(url);
};

const NominasPorColaborador = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingColaboradores, setLoadingColaboradores] = useState(false);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);
  const [sujetosDeNomina, setSujetosDeNomina] = useState(SUJETOS_DE_NOMINA_FALLBACK);
  const [colaboradores, setColaboradores] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [reporte, setReporte] = useState(null);
  const fechaDeConsulta = useMemo(() => moment(), []);

  const sujeto = Form.useWatch('sujeto', form);
  const colaborador = Form.useWatch('colaborador', form);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      try {
        setLoading(true);
        await checkToken();
        const [_sujetos] = await Promise.all([getSujetosDeNomina()]);
        if (mounted && Array.isArray(_sujetos) && _sujetos.length) {
          setSujetosDeNomina(_sujetos);
        }
        if (mounted) setLoading(false);
      } catch (err) {
        onError(err, setLoading);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setColaboradores([]);
    setReporte(null);
    form.setFieldsValue({ colaborador: undefined });
    if (!sujeto) return () => { mounted = false; };
    const fetchColaboradores = async () => {
      setLoadingColaboradores(true);
      const _colaboradores = await getColaboradoresPorSujeto(sujeto);
      if (mounted) {
        setColaboradores(Array.isArray(_colaboradores) ? _colaboradores : []);
        setLoadingColaboradores(false);
      }
    };
    fetchColaboradores();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [sujeto]);

  useEffect(() => {
    let mounted = true;
    setPeriodos([]);
    setReporte(null);
    form.setFieldsValue({ periodo_inicial: undefined, periodo_final: undefined });
    if (!sujeto || !colaborador) return () => { mounted = false; };
    const fetchPeriodos = async () => {
      setLoadingPeriodos(true);
      const _periodos = await getPeriodosPorColaborador({ sujeto, colaborador });
      if (!mounted) return;
      const lista = Array.isArray(_periodos) ? _periodos : [];
      setPeriodos(lista);
      setLoadingPeriodos(false);
      if (lista.length) {
        form.setFieldsValue({
          periodo_inicial: lista[0].id,
          periodo_final: lista[lista.length - 1].id,
        });
      } else {
        message.info('El colaborador no tiene nóminas autorizadas.');
      }
    };
    fetchPeriodos();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [sujeto, colaborador]);

  const indicePeriodo = (id) => periodos.findIndex((p) => p.id === id);

  const onConsultar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const response = await getReporteNominasPorColaborador({
        sujeto: values.sujeto,
        colaborador: values.colaborador,
        periodo_inicial: values.periodo_inicial,
        periodo_final: values.periodo_final,
      });
      setReporte(response);
      if (response && !response.registros?.length) {
        message.info('No se encontraron nóminas autorizadas en el rango de periodos seleccionado.');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const conceptos = reporte?.conceptos || [];
  const registros = reporte?.registros || [];

  // Si en un mismo periodo hay más de una nómina (ordinaria + extraordinaria), se
  // agrega el folio de la nómina a la etiqueta para poder distinguir las filas.
  const etiquetaPeriodo = useMemo(() => {
    const conteo = {};
    registros.forEach((r) => { conteo[r.periodo_id] = (conteo[r.periodo_id] || 0) + 1; });
    return (registro) => (conteo[registro.periodo_id] > 1 && registro.numero_nomina
      ? `${registro.periodo} - ${registro.numero_nomina}`
      : registro.periodo);
  }, [registros]);

  const totales = useMemo(() => {
    const acumulado = {};
    registros.forEach((registro) => {
      conceptos.forEach(({ key }) => {
        acumulado[key] = redondear((acumulado[key] || 0) + (registro.conceptos?.[key] || 0));
      });
      COLUMNAS_TOTALES.forEach(({ key }) => {
        acumulado[key] = redondear((acumulado[key] || 0) + (registro[key] || 0));
      });
    });
    return acumulado;
  }, [registros, conceptos]);

  const llavesImporte = [...conceptos.map((c) => c.key), ...COLUMNAS_TOTALES.map((c) => c.key)];

  const columns = useMemo(() => [
    {
      title: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      fixed: 'left',
      width: 260,
      render: (_, record) => etiquetaPeriodo(record),
    },
    {
      title: 'Plaza',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 260,
    },
    ...conceptos.map((concepto) => ({
      title: concepto.descripcion,
      key: concepto.key,
      width: 160,
      align: 'right',
      render: (_, record) => currencyFormatter(record.conceptos?.[concepto.key] || 0),
    })),
    ...COLUMNAS_TOTALES.map(({ key, title }) => ({
      title,
      dataIndex: key,
      key,
      width: 160,
      align: 'right',
      render: (value) => <strong>{currencyFormatter(value)}</strong>,
    })),
  ], [conceptos, etiquetaPeriodo]);

  const etiquetaSujeto = sujetosDeNomina.find((s) => s.slug === sujeto)?.etiqueta || '';
  const colaboradorSeleccionado = colaboradores.find((c) => c.id === colaborador);
  const nombreColaborador = reporte?.colaborador
    || (colaboradorSeleccionado ? renderColaborador(sujeto)(colaboradorSeleccionado) : '');

  const onExportarExcel = async () => {
    try {
      if (!registros.length) {
        message.warn('No hay información para exportar.');
        return;
      }
      setLoading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Nominas por Colaborador');
      const headers = [
        'Periodo',
        'Plaza',
        ...conceptos.map((c) => c.descripcion),
        ...COLUMNAS_TOTALES.map((c) => c.title),
      ];
      const totalColumnas = headers.length;
      const primeraColumnaImporte = 3;

      worksheet.mergeCells(1, 1, 1, Math.max(totalColumnas, 4));
      const titulo = worksheet.getCell(1, 1);
      titulo.value = 'REPORTE DE CONSULTA DE NÓMINAS POR COLABORADOR';
      titulo.font = { bold: true, size: 14 };
      titulo.alignment = { horizontal: 'center' };

      const encabezados = [
        ['Tipo de Colaborador', etiquetaSujeto, 'Fecha de Consulta', fechaDeConsulta.toDate()],
        ['Colaborador', nombreColaborador, null, null],
        ['Periodo Inicial', reporte?.periodo_inicial || '', 'Periodo Final', reporte?.periodo_final || ''],
      ];
      encabezados.forEach((fila, idx) => {
        const row = worksheet.getRow(3 + idx);
        row.values = fila;
        [1, 3].forEach((col) => { row.getCell(col).font = { bold: true }; });
      });
      worksheet.getCell(3, 4).numFmt = 'dd/mm/yyyy';
      worksheet.getCell(3, 4).alignment = { horizontal: 'left' };

      const filaEncabezado = 7;
      const headerRow = worksheet.getRow(filaEncabezado);
      headerRow.values = headers;
      headerRow.eachCell((cell) => {
        /* eslint-disable no-param-reassign */
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E78' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { bottom: { style: 'thin' } };
        /* eslint-enable no-param-reassign */
      });
      headerRow.height = 32;

      registros.forEach((registro) => {
        worksheet.addRow([
          etiquetaPeriodo(registro),
          registro.plaza,
          ...conceptos.map((c) => redondear(registro.conceptos?.[c.key] || 0)),
          ...COLUMNAS_TOTALES.map((c) => redondear(registro[c.key])),
        ]);
      });

      // Fila de TOTALES con fórmulas SUM para que el usuario pueda seguir operando en Excel.
      const primeraFilaDatos = filaEncabezado + 1;
      const ultimaFilaDatos = filaEncabezado + registros.length;
      const totalesRow = worksheet.addRow(['TOTALES']);
      llavesImporte.forEach((key, idx) => {
        const col = primeraColumnaImporte + idx;
        const letra = worksheet.getColumn(col).letter;
        totalesRow.getCell(col).value = {
          formula: `SUM(${letra}${primeraFilaDatos}:${letra}${ultimaFilaDatos})`,
          result: totales[key] || 0,
        };
      });
      totalesRow.eachCell((cell) => {
        /* eslint-disable no-param-reassign */
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' } };
        /* eslint-enable no-param-reassign */
      });

      // Formato moneda solo en celdas de importes (no a nivel columna: pisaría el
      // formato de fecha de "Fecha de Consulta", que comparte columna).
      for (let col = primeraColumnaImporte; col <= totalColumnas; col += 1) {
        for (let fila = primeraFilaDatos; fila <= totalesRow.number; fila += 1) {
          worksheet.getCell(fila, col).numFmt = FORMATO_MONEDA_EXCEL;
        }
        worksheet.getColumn(col).width = 18;
      }
      worksheet.getColumn(1).width = 42;
      worksheet.getColumn(2).width = 40;
      worksheet.views = [{ state: 'frozen', xSplit: 2, ySplit: filaEncabezado }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      descargarBlobComoArchivo(blob, `Nominas_por_Colaborador_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card bordered={false}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ sujeto: 'empleado' }}
            onFinish={onConsultar}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="sujeto"
                  label="Tipo de Colaborador"
                  rules={[{ required: true, message: 'El campo es requerido' }]}
                >
                  <Select
                    dataSource={sujetosDeNomina}
                    keyProp="slug"
                    valueProp="slug"
                    labelProp="etiqueta"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item label="Fecha de Consulta">
                  <DatePicker
                    value={fechaDeConsulta}
                    format="DD/MM/YYYY"
                    disabled
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="colaborador"
                  label="Colaborador"
                  rules={[{ required: true, message: 'El campo es requerido' }]}
                >
                  <Select
                    dataSource={colaboradores}
                    keyProp="id"
                    render={renderColaborador(sujeto)}
                    loading={loadingColaboradores}
                    disabled={!sujeto}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodo_inicial"
                  label="Periodo Inicial"
                  rules={[{ required: true, message: 'El campo es requerido' }]}
                >
                  <Select
                    dataSource={periodos}
                    keyProp="id"
                    labelProp="descripcion"
                    loading={loadingPeriodos}
                    disabled={!colaborador}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="periodo_final"
                  label="Periodo Final"
                  dependencies={['periodo_inicial']}
                  rules={[
                    { required: true, message: 'El campo es requerido' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const inicial = getFieldValue('periodo_inicial');
                        if (!value || !inicial || indicePeriodo(value) >= indicePeriodo(inicial)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('El Periodo Final no puede ser anterior al Periodo Inicial'));
                      },
                    }),
                  ]}
                >
                  <Select
                    dataSource={periodos}
                    keyProp="id"
                    labelProp="descripcion"
                    loading={loadingPeriodos}
                    disabled={!colaborador}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10} justify="end">
              <Col>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  Consultar
                </Button>
              </Col>
              <Col>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={onExportarExcel}
                  disabled={!registros.length}
                >
                  Exportar a Excel
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card bordered={false} style={{ marginTop: 10 }}>
          {reporte?.colaborador && (
            <Typography.Title level={5}>{reporte.colaborador}</Typography.Title>
          )}
          <Table
            rowKey="id"
            size="small"
            bordered
            columns={columns}
            dataSource={registros}
            pagination={false}
            scroll={{ x: 'max-content', y: 520 }}
            locale={{ emptyText: <Empty description="Seleccione los filtros y pulse Consultar" /> }}
            summary={() => (registros.length ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <strong>TOTALES</strong>
                  </Table.Summary.Cell>
                  {llavesImporte.map((key, idx) => (
                    <Table.Summary.Cell key={key} index={idx + 2} align="right">
                      <strong>{currencyFormatter(totales[key])}</strong>
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            ) : null)}
          />
        </Card>
      </Spin>
    </Row>
  );
};

export default NominasPorColaborador;
